import React, { useState, useEffect } from 'react';
import { S3Client, ListObjectsV2Command, GetObjectCommand } from '@aws-sdk/client-s3';
import { Auth } from 'aws-amplify';
import { convertAndDownloadKeywordData } from '../utils/keywordToCSV';
import './KeywordDataBrowser.css';

const BUCKET_NAME = 'prod-keyword-research-data-038462795459';
const REGION = 'us-east-1';
const IDENTITY_POOL_ID = 'us-east-1:206af41a-c7e1-45c3-99bd-b9168d0b8e1b';

const KeywordDataBrowser = ({ company }) => {
  const [s3Client, setS3Client] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filesByDate, setFilesByDate] = useState({});
  const [filesByMonth, setFilesByMonth] = useState({});
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [expandedDates, setExpandedDates] = useState(new Set());
  const [previewData, setPreviewData] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Initialize S3 client with Cognito credentials
  useEffect(() => {
    const initS3Client = async () => {
      try {
        // Get credentials from Amplify Auth
        const credentials = await Auth.currentCredentials();

        // Create S3 client with the actual credentials
        const client = new S3Client({
          region: REGION,
          credentials: {
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken,
          },
        });

        setS3Client(client);
      } catch (err) {
        console.error('Error initializing S3 client:', err);
        setError('Failed to initialize S3 client: ' + err.message);
      }
    };

    initS3Client();
  }, []);

  // Load files from S3 when client is ready
  useEffect(() => {
    if (s3Client && company?.s3BrandFolder) {
      loadFiles();
    }
  }, [s3Client, company]);

  const loadFiles = async () => {
    if (!company?.s3BrandFolder) {
      setError('No S3 brand folder configured for this client. Please contact support.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const prefix = `brands/${company.s3BrandFolder}/jungle-scout/keywords_by_asin/`;

      const command = new ListObjectsV2Command({
        Bucket: BUCKET_NAME,
        Prefix: prefix,
      });

      const response = await s3Client.send(command);

      if (!response.Contents || response.Contents.length === 0) {
        setError('No keyword data files found for this client.');
        setFiles([]);
        setFilesByDate({});
        return;
      }

      // Process and organize files by date
      const processedFiles = response.Contents
        .filter(item => item.Key.endsWith('.json'))
        .map(item => {
          // Extract date and ASIN from path
          // Format: brands/{brand}/jungle-scout/keywords_by_asin/{year}/{month}/{day}/{hour}/{ASIN}.json
          const parts = item.Key.split('/');
          const year = parts[4];
          const month = parts[5];
          const day = parts[6];
          const hour = parts[7];
          const filename = parts[8];
          const asin = filename.replace('.json', '');

          return {
            key: item.Key,
            asin,
            filename,
            date: `${year}-${month}-${day}`,
            hour,
            dateTime: new Date(`${year}-${month}-${day}T${hour}:00:00`),
            size: item.Size,
            lastModified: item.LastModified,
          };
        })
        .sort((a, b) => b.dateTime - a.dateTime); // Most recent first

      setFiles(processedFiles);

      // Group files by date
      const groupedByDate = processedFiles.reduce((acc, file) => {
        const dateKey = file.date;
        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }
        acc[dateKey].push(file);
        return acc;
      }, {});

      setFilesByDate(groupedByDate);

      // Group files by month/year for hierarchical view
      const groupedByMonth = processedFiles.reduce((acc, file) => {
        // Extract year and month from date (format: YYYY-MM-DD)
        const [year, month] = file.date.split('-');
        const monthKey = `${year}-${month}`; // e.g., "2025-10"

        if (!acc[monthKey]) {
          acc[monthKey] = {
            dates: {},
            totalFiles: 0
          };
        }

        if (!acc[monthKey].dates[file.date]) {
          acc[monthKey].dates[file.date] = [];
        }

        acc[monthKey].dates[file.date].push(file);
        acc[monthKey].totalFiles++;

        return acc;
      }, {});

      setFilesByMonth(groupedByMonth);
    } catch (err) {
      console.error('Error loading files:', err);
      setError(`Failed to load files: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to fetch and parse file data from S3
  const fetchFileData = async (file) => {
    const command = new GetObjectCommand({
      Bucket: BUCKET_NAME,
      Key: file.key,
    });

    const response = await s3Client.send(command);

    // Convert ReadableStream to string (browser environment)
    const streamToString = async (stream) => {
      const reader = stream.getReader();
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
      }

      // Combine chunks into a single Uint8Array
      const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
      const combined = new Uint8Array(totalLength);
      let offset = 0;
      for (const chunk of chunks) {
        combined.set(chunk, offset);
        offset += chunk.length;
      }

      // Decode to string
      return new TextDecoder('utf-8').decode(combined);
    };

    const bodyContents = await streamToString(response.Body);
    const jsonData = JSON.parse(bodyContents);

    // Extract the data array from the wrapper object
    return jsonData.data || jsonData;
  };

  const previewFileData = async (file) => {
    try {
      setLoading(true);
      const keywordArray = await fetchFileData(file);

      setPreviewData(keywordArray);
      setPreviewFile(file);
      setShowPreview(true);
    } catch (err) {
      console.error('Error previewing file:', err);
      alert(`Failed to preview file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadFileAsCSV = async (file) => {
    try {
      setLoading(true);
      const keywordArray = await fetchFileData(file);

      // Convert to CSV and download (using specialized keyword converter)
      const filename = `${company.name}_${file.asin}_${file.date}_${file.hour}.csv`;
      convertAndDownloadKeywordData(keywordArray, filename);

    } catch (err) {
      console.error('Error downloading file:', err);
      alert(`Failed to download file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const downloadPreviewAsCSV = () => {
    if (previewData && previewFile) {
      const filename = `${company.name}_${previewFile.asin}_${previewFile.date}_${previewFile.hour}.csv`;
      convertAndDownloadKeywordData(previewData, filename);
    }
  };

  const closePreview = () => {
    setShowPreview(false);
    setPreviewData(null);
    setPreviewFile(null);
  };

  const toggleMonthExpansion = (monthKey) => {
    const newExpanded = new Set(expandedMonths);
    if (newExpanded.has(monthKey)) {
      newExpanded.delete(monthKey);
    } else {
      newExpanded.add(monthKey);
    }
    setExpandedMonths(newExpanded);
  };

  const toggleDateExpansion = (date) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatMonthYear = (monthKey) => {
    // monthKey format: "2025-10"
    const [year, month] = monthKey.split('-');
    // Use Date constructor with year, month-1, day to avoid timezone issues
    // JavaScript months are 0-indexed (0 = January, 11 = December)
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long'
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!company?.s3BrandFolder) {
    return (
      <div className="keyword-data-browser">
        <div className="empty-state">
          <h3>AWS S3 Brand Folder Not Configured</h3>
          <p>This client does not have an S3 brand folder mapping configured.</p>
          <p>Please contact support to set up the brand folder for keyword data access.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="keyword-data-browser">
      <div className="browser-header">
        <h3>Keyword Research Data</h3>
        <div className="header-info">
          <span className="brand-label">Brand Folder:</span>
          <span className="brand-name">{company.s3BrandFolder}</span>
          <button
            onClick={loadFiles}
            className="btn-refresh"
            disabled={loading}
          >
            {loading ? '↻ Loading...' : '↻ Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <strong>Error:</strong> {error}
        </div>
      )}

      {loading && files.length === 0 ? (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading keyword data files...</p>
        </div>
      ) : files.length === 0 ? (
        <div className="empty-state">
          <p>No keyword data files found.</p>
        </div>
      ) : (
        <div className="files-container">
          <div className="files-summary">
            <p>Found {files.length} keyword data files across {Object.keys(filesByMonth).length} months</p>
          </div>

          <div className="files-by-month">
            {Object.keys(filesByMonth)
              .sort((a, b) => b.localeCompare(a)) // Most recent months first
              .map(monthKey => (
                <div key={monthKey} className="month-group">
                  {/* Month Header */}
                  <div
                    className="month-header"
                    onClick={() => toggleMonthExpansion(monthKey)}
                  >
                    <span className="expand-icon">
                      {expandedMonths.has(monthKey) ? '▼' : '▶'}
                    </span>
                    <span className="month-label">{formatMonthYear(monthKey)}</span>
                    <span className="file-count">
                      {filesByMonth[monthKey].totalFiles} file{filesByMonth[monthKey].totalFiles !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Dates within Month */}
                  {expandedMonths.has(monthKey) && (
                    <div className="dates-container">
                      {Object.keys(filesByMonth[monthKey].dates)
                        .sort((a, b) => b.localeCompare(a)) // Most recent dates first
                        .map(date => (
                          <div key={date} className="date-group">
                            <div
                              className="date-header"
                              onClick={() => toggleDateExpansion(date)}
                            >
                              <span className="expand-icon">
                                {expandedDates.has(date) ? '▼' : '▶'}
                              </span>
                              <span className="date-label">{formatDate(date)}</span>
                              <span className="file-count">
                                {filesByMonth[monthKey].dates[date].length} file{filesByMonth[monthKey].dates[date].length !== 1 ? 's' : ''}
                              </span>
                            </div>

                            {expandedDates.has(date) && (
                              <div className="files-list">
                                <table className="files-table">
                                  <thead>
                                    <tr>
                                      <th>ASIN</th>
                                      <th>Hour</th>
                                      <th>Size</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {filesByMonth[monthKey].dates[date]
                                      .sort((a, b) => b.hour.localeCompare(a.hour))
                                      .map(file => (
                                        <tr key={file.key}>
                                          <td className="asin-cell">
                                            <code>{file.asin}</code>
                                          </td>
                                          <td>{file.hour}:00</td>
                                          <td>{formatFileSize(file.size)}</td>
                                          <td>
                                            <div className="action-buttons">
                                              <button
                                                className="btn-preview"
                                                onClick={() => previewFileData(file)}
                                                disabled={loading}
                                              >
                                                Preview
                                              </button>
                                              <button
                                                className="btn-download"
                                                onClick={() => downloadFileAsCSV(file)}
                                                disabled={loading}
                                              >
                                                Download
                                              </button>
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewData && previewFile && (
        <div className="preview-modal-overlay" onClick={closePreview}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-header">
              <div className="preview-title">
                <h3>Preview: {previewFile.asin}</h3>
                <p className="preview-info">
                  {formatDate(previewFile.date)} at {previewFile.hour}:00 • {previewData.length} rows
                </p>
              </div>
              <button className="btn-close" onClick={closePreview}>✕</button>
            </div>

            <div className="preview-body">
              <div className="preview-table-container">
                <table className="preview-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Monthly Search (Exact)</th>
                      <th>Monthly Search (Broad)</th>
                      <th>Organic Rank</th>
                      <th>Sponsored Rank</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 100).map((row, idx) => {
                      // Parse attributes if it's a string
                      let attributes = row.attributes;
                      if (typeof attributes === 'string') {
                        try {
                          attributes = JSON.parse(attributes);
                        } catch (e) {
                          attributes = {};
                        }
                      }

                      return (
                        <tr key={idx}>
                          <td>{attributes.name || row.id || '-'}</td>
                          <td className="number-cell">
                            {attributes.monthly_search_volume_exact?.toLocaleString() || '-'}
                          </td>
                          <td className="number-cell">
                            {attributes.monthly_search_volume_broad?.toLocaleString() || '-'}
                          </td>
                          <td className="number-cell">
                            {attributes.organic_rank !== null && attributes.organic_rank !== undefined
                              ? attributes.organic_rank
                              : '-'}
                          </td>
                          <td className="number-cell">
                            {attributes.sponsored_rank !== null && attributes.sponsored_rank !== undefined
                              ? attributes.sponsored_rank
                              : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {previewData.length > 100 && (
                <div className="preview-notice">
                  Showing first 100 rows of {previewData.length} total rows. Download CSV to see all data.
                </div>
              )}
            </div>

            <div className="preview-footer">
              <button className="btn-cancel" onClick={closePreview}>Close</button>
              <button className="btn-download-primary" onClick={downloadPreviewAsCSV}>
                Download CSV
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeywordDataBrowser;
